class CollectionGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector("#product-grid");
    this.toolbar = this.querySelector("[data-toolbar]");
    this.sortSelect = this.querySelector("[data-sort]");
    this.sortLabel = this.querySelector("[data-sort-label]");
    if (!this.grid) return;

    this.cards = Array.from(this.grid.querySelectorAll("[data-product]")).map((el) => ({
      el,
      price: parseFloat(el.dataset.price || "0") || 0,
    }));
    this.promos = Array.from(this.grid.querySelectorAll("[data-promo]")).map((el) => ({
      el,
      mobile: this.position(el.dataset.posMobile),
      tablet: this.position(el.dataset.posTablet),
      desktop: this.position(el.dataset.posDesktop),
    }));
    this.breakpoint = this.currentBreakpoint();

    const tabs = this.querySelector(".collection-toolbar__tabs");
    const activeTab = tabs ? tabs.querySelector('[aria-current="page"]') : null;
    if (activeTab) tabs.scrollLeft = activeTab.offsetLeft - tabs.offsetLeft - 20;

    if (this.sortSelect) {
      this.sortSelect.value = new URL(window.location.href).searchParams.get("sort_by") || "";
      this.sortSelect.addEventListener("change", () => this.onSortChange());
    }

    this.initSortMenu();

    this.onResize = () => {
      const next = this.currentBreakpoint();
      if (next === this.breakpoint) return;
      this.breakpoint = next;
      this.render();
    };
    window.addEventListener("resize", this.onResize);

    this.onPopState = () => {
      if (this.sortSelect) {
        this.sortSelect.value = new URL(window.location.href).searchParams.get("sort_by") || "";
      }
      this.render();
    };
    window.addEventListener("popstate", this.onPopState);

    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("popstate", this.onPopState);
    if (this.onOutsideSort) document.removeEventListener("pointerdown", this.onOutsideSort);
  }

  initSortMenu() {
    this.sortRoot = this.querySelector("[data-sort-root]");
    this.sortButton = this.querySelector("[data-sort-button]");
    this.sortMenu = this.querySelector("[data-sort-menu]");
    if (!this.sortRoot || !this.sortButton || !this.sortMenu || !this.sortSelect) return;
    this.sortOptions = Array.from(this.sortMenu.querySelectorAll("[data-sort-option]"));
    this.activeOption = -1;

    this.sortButton.addEventListener("click", () => {
      if (this.sortMenu.classList.contains("is-open")) this.closeSortMenu();
      else this.openSortMenu();
    });
    this.sortButton.addEventListener("keydown", (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      this.openSortMenu();
    });

    this.sortOptions.forEach((option, index) => {
      option.addEventListener("click", () => this.chooseSort(option.dataset.sortOption));
      option.addEventListener("mouseenter", () => this.setActiveOption(index));
    });

    this.sortMenu.addEventListener("keydown", (e) => {
      const last = this.sortOptions.length - 1;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setActiveOption(this.activeOption >= last ? 0 : this.activeOption + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setActiveOption(this.activeOption <= 0 ? last : this.activeOption - 1);
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const option = this.sortOptions[this.activeOption];
        if (option) this.chooseSort(option.dataset.sortOption);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.closeSortMenu(true);
      } else if (e.key === "Tab") {
        this.closeSortMenu();
      }
    });

    this.onOutsideSort = (e) => {
      if (!this.sortRoot.contains(e.target)) this.closeSortMenu();
    };
    document.addEventListener("pointerdown", this.onOutsideSort);
  }

  openSortMenu() {
    this.sortMenu.classList.add("is-open");
    this.sortButton.setAttribute("aria-expanded", "true");
    const selected = this.sortOptions.findIndex((o) => o.dataset.sortOption === this.sortSelect.value);
    this.setActiveOption(selected >= 0 ? selected : 0);
    this.sortMenu.focus({ preventScroll: true });
  }

  closeSortMenu(returnFocus) {
    if (!this.sortMenu || !this.sortMenu.classList.contains("is-open")) return;
    this.sortMenu.classList.remove("is-open");
    this.sortButton.setAttribute("aria-expanded", "false");
    if (returnFocus) this.sortButton.focus();
  }

  setActiveOption(index) {
    this.activeOption = index;
    this.sortOptions.forEach((option, i) => {
      option.classList.toggle("is-active", i === index);
      if (i === index) this.sortMenu.setAttribute("aria-activedescendant", option.id);
    });
  }

  chooseSort(value) {
    this.closeSortMenu(true);
    if (this.sortSelect.value === value) return;
    this.sortSelect.value = value;
    this.onSortChange();
  }

  position(value) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  }

  currentBreakpoint() {
    if (window.innerWidth >= 1024) return "desktop";
    if (window.innerWidth >= 768) return "tablet";
    return "mobile";
  }

  onSortChange() {
    const value = this.sortSelect.value;
    const url = new URL(window.location.href);
    if (value) url.searchParams.set("sort_by", value);
    else url.searchParams.delete("sort_by");

    if (!value.startsWith("price-")) {
      window.location.assign(url);
      return;
    }

    window.history.pushState({}, "", url);
    this.render();
    this.scrollToGrid();
  }

  scrollToGrid() {
    if (!this.toolbar) return;
    const stickyTop = parseFloat(getComputedStyle(this.toolbar).top) || 0;
    const top = this.grid.getBoundingClientRect().top + window.scrollY - stickyTop - this.toolbar.offsetHeight - 20;
    if (window.scrollY > top) window.scrollTo({ top, behavior: "auto" });
  }

  sortedCards() {
    const value = this.sortSelect ? this.sortSelect.value : "";
    if (value === "price-ascending") return [...this.cards].sort((a, b) => a.price - b.price);
    if (value === "price-descending") return [...this.cards].sort((a, b) => b.price - a.price);
    return this.cards;
  }

  render() {
    if (this.sortLabel && this.sortSelect) {
      const option = this.sortSelect.options[this.sortSelect.selectedIndex];
      this.sortLabel.textContent = option ? option.text : "Sort by";
    }
    if (this.sortOptions) {
      this.sortOptions.forEach((option) => {
        option.setAttribute("aria-selected", option.dataset.sortOption === this.sortSelect.value ? "true" : "false");
      });
    }

    const promos = this.promos
      .filter((promo) => promo[this.breakpoint] !== null)
      .sort((a, b) => a[this.breakpoint] - b[this.breakpoint]);
    const fragment = document.createDocumentFragment();
    let next = 0;

    this.sortedCards().forEach((card, index) => {
      while (next < promos.length && promos[next][this.breakpoint] <= index) {
        fragment.appendChild(promos[next].el);
        next += 1;
      }
      fragment.appendChild(card.el);
    });
    while (next < promos.length) {
      fragment.appendChild(promos[next].el);
      next += 1;
    }

    this.promos.forEach((promo) => {
      promo.el.style.display = promo[this.breakpoint] === null ? "none" : "";
    });
    this.grid.appendChild(fragment);
  }
}

customElements.define("collection-grid", CollectionGrid);
